import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Button, SettingsGroup, SettingsItemAutocomplete } from '@elipssolution/harfang';
import { styled } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import SettingsDialogPage from '../../../../../../components/SettingsDialogPage';
import { useSettingsDialog } from '../../../../../../hooks/useSettingsDialog';
import { CustomerFileType } from '../../../../../../types/customerFile';
import { generateErrorInformations } from '../../../../../../utils/errorHandler';
import { SUCCESS_ANIMATION_DELAY } from '../../../../../../utils/statusAnimationDelay';
import { FETCH_JOURNALS_BY_CUSTOMER_FILE, FetchJournalsByCustomerFileType } from '../../../../api/journal';
import {
	CREATE_CUSTOMER_FILE_BILL_EXCHANGE_SETTING_JOURNAL,
	CREATE_CUSTOMER_FILE_CHECK_SETTING_JOURNAL,
	CreateCustomerFileBillExchangeSettingJournalType,
	CreateCustomerFileCheckSettingJournalType,
	FETCH_CUSTOMER_FILE_BILL_EXCHANGE_SETTING_JOURNAL,
	FETCH_CUSTOMER_FILE_CHECK_SETTING_JOURNAL,
	FetchCustomerFileBillExchangeSettingJournalType,
	FetchCustomerFileCheckSettingJournalType,
} from '../../../../api/settingJournal';
import { CheckTypeEnum } from '../../../../types/check';
import { JournalType } from '../../../../types/journal';

const ActionWrapper = styled('div')({
	display: 'flex',
	flexDirection: 'row-reverse',
	justifyContent: 'space-between',
});

const ErrorWrapper = styled('div')(({ theme: { palette, shape } }) => ({
	height: 36,
	width: '50%',

	display: 'grid',
	placeItems: 'center',

	color: palette.error.main,
	backgroundColor: `${palette.error.main}1A`,
	borderRadius: shape.borderRadius,
}));

type FormFieldType = {
	billExchangeJournal?: JournalType;
	checkIssuanceJournal?: JournalType;
	checkRemittanceJournal?: JournalType;
};

type JournalCodesProps = {
	customerFileId?: CustomerFileType['id'];
};

const JournalCodes = ({ customerFileId }: JournalCodesProps) => {
	const { onHasDirtyFormChange } = useSettingsDialog();

	const {
		control,
		formState: { isDirty, isValid },
		handleSubmit,
		setValue,
		reset,
	} = useForm<FormFieldType>();

	const [error, setError] = useState<string>();

	const isFormValid = useMemo(() => isDirty && isValid, [isDirty, isValid]);

	const [fetchJournalsByCustomerFile] = useLazyQuery<FetchJournalsByCustomerFileType>(FETCH_JOURNALS_BY_CUSTOMER_FILE);

	const journalsDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
		): Promise<{
			count: number;
			items: JournalType[];
		}> => {
			const { data, error: queryError } = await fetchJournalsByCustomerFile({
				variables: {
					customerFileId,
					page: {
						limit,
						offset,
					},
					search,
				},
			});

			if (queryError) {
				throw queryError;
			}

			const {
				journalsByCustomerFile: { count = 0, items = [] },
			} = data ?? {
				journalsByCustomerFile: {},
			};

			return {
				count,
				items,
			};
		},
		[fetchJournalsByCustomerFile, customerFileId],
	);

	const { data: fetchedCustomerFileBillExchangeSettingJournalData } =
		useQuery<FetchCustomerFileBillExchangeSettingJournalType>(FETCH_CUSTOMER_FILE_BILL_EXCHANGE_SETTING_JOURNAL, {
			onCompleted: ({ quickentry_settingCustomerFileBillExchangeJournal: { journal } }) =>
				setValue('billExchangeJournal', journal ?? undefined),
			onError: (queryError) =>
				setError(
					generateErrorInformations({
						error: queryError,
						resource: 'quickentry_settingCustomerFileBillExchangeJournal',
					}).message,
				),
			skip: !customerFileId,
			variables: {
				id: customerFileId,
			},
		});

	const { data: fetchedCustomerFileCheckIssuanceSettingJournalData } =
		useQuery<FetchCustomerFileCheckSettingJournalType>(FETCH_CUSTOMER_FILE_CHECK_SETTING_JOURNAL, {
			onCompleted: ({ quickentry_settingCustomerFileCheckJournal: { journal } }) =>
				setValue('checkIssuanceJournal', journal ?? undefined),
			onError: (queryError) =>
				setError(
					generateErrorInformations({ error: queryError, resource: 'quickentry_settingCustomerFileCheckJournal' })
						.message,
				),
			skip: !customerFileId,
			variables: {
				id: customerFileId,
				checkType: CheckTypeEnum.CHECK_ISSUANCE,
			},
		});

	const { data: fetchedCustomerFileCheckRemittanceSettingJournalData } =
		useQuery<FetchCustomerFileCheckSettingJournalType>(FETCH_CUSTOMER_FILE_CHECK_SETTING_JOURNAL, {
			onCompleted: ({ quickentry_settingCustomerFileCheckJournal: { journal } }) =>
				setValue('checkRemittanceJournal', journal ?? undefined),
			onError: (queryError) =>
				setError(
					generateErrorInformations({
						error: queryError,
						resource: 'quickentry_settingCustomerFileCheckJournal',
					}).message,
				),
			skip: !customerFileId,
			variables: {
				id: customerFileId,
				checkType: CheckTypeEnum.CHECK_REMITTANCE,
			},
		});

	const { quickentry_settingCustomerFileBillExchangeJournal } = fetchedCustomerFileBillExchangeSettingJournalData || {};
	const { journal: billExchangeJournal, inheritedJournal: inheritedBillExchangeJournal } =
		quickentry_settingCustomerFileBillExchangeJournal || {};

	const { quickentry_settingCustomerFileCheckJournal: settingCustomerFileCheckIssuanceJournal } =
		fetchedCustomerFileCheckIssuanceSettingJournalData || {};
	const { journal: checkIssuanceJournal, inheritedJournal: inheritedCheckIssuanceJournal } =
		settingCustomerFileCheckIssuanceJournal || {};

	const { quickentry_settingCustomerFileCheckJournal: settingCustomerFileCheckRemittanceJournal } =
		fetchedCustomerFileCheckRemittanceSettingJournalData || {};
	const { journal: checkRemittanceJournal, inheritedJournal: inheritedCheckRemittanceJournal } =
		settingCustomerFileCheckRemittanceJournal || {};

	const [
		createCustomerFileBillExchangeJournal,
		{ data: createCustomerFileBillExchangeJournalData, reset: resetCreateCustomerFileBillExchangeJournal },
	] = useMutation<CreateCustomerFileBillExchangeSettingJournalType>(CREATE_CUSTOMER_FILE_BILL_EXCHANGE_SETTING_JOURNAL);

	const [
		createCustomerFileCheckIssuanceJournal,
		{ data: createCustomerFileCheckIssuanceJournalData, reset: resetCreateCustomerFileCheckIssuanceJournal },
	] = useMutation<CreateCustomerFileCheckSettingJournalType>(CREATE_CUSTOMER_FILE_CHECK_SETTING_JOURNAL);

	const [
		createCustomerFileCheckRemittanceJournal,
		{ data: createCustomerFileCheckRemittanceJournalData, reset: resetCreateCustomerFileCheckRemittanceJournal },
	] = useMutation<CreateCustomerFileCheckSettingJournalType>(CREATE_CUSTOMER_FILE_CHECK_SETTING_JOURNAL);

	const hasMutationSucceeded = useMemo(
		() =>
			!!createCustomerFileBillExchangeJournalData ||
			!!createCustomerFileCheckIssuanceJournalData ||
			!!createCustomerFileCheckRemittanceJournalData,
		[
			createCustomerFileBillExchangeJournalData,
			createCustomerFileCheckIssuanceJournalData,
			createCustomerFileCheckRemittanceJournalData,
		],
	);

	const onSubmit = useCallback(
		(values: FormFieldType) => {
			const {
				billExchangeJournal: formBillExchangeJournal,
				checkIssuanceJournal: formCheckIssuanceJournal,
				checkRemittanceJournal: formCheckRemittanceJournal,
			} = values;

			const submitActions = [];

			if (formBillExchangeJournal?.id !== billExchangeJournal?.id) {
				const { id: formBillExchangeJournalId } = formBillExchangeJournal ?? {};

				submitActions.push(
					createCustomerFileBillExchangeJournal({
						variables: {
							createSettingCustomerFileBillExchangeJournalInput: {
								customerFileId,
								journalId: formBillExchangeJournalId ?? undefined,
							},
						},
					}),
				);
			}

			if (formCheckIssuanceJournal?.id !== checkIssuanceJournal?.id) {
				const { id: formCheckIssuanceJournalId } = formCheckIssuanceJournal ?? {};

				submitActions.push(
					createCustomerFileCheckIssuanceJournal({
						variables: {
							createSettingCustomerFileCheckJournalInput: {
								customerFileId,
								journalId: formCheckIssuanceJournalId ?? undefined,
								checkType: CheckTypeEnum.CHECK_ISSUANCE,
							},
						},
					}),
				);
			}

			if (formCheckRemittanceJournal?.id !== checkRemittanceJournal?.id) {
				const { id: formCheckRemittanceJournalId } = formCheckRemittanceJournal ?? {};

				submitActions.push(
					createCustomerFileCheckRemittanceJournal({
						variables: {
							createSettingCustomerFileCheckJournalInput: {
								customerFileId,
								journalId: formCheckRemittanceJournalId ?? undefined,
								checkType: CheckTypeEnum.CHECK_REMITTANCE,
							},
						},
					}),
				);
			}

			(async () => {
				await Promise.all(submitActions);

				reset({
					billExchangeJournal: formBillExchangeJournal,
					checkIssuanceJournal: formCheckIssuanceJournal,
					checkRemittanceJournal: formCheckRemittanceJournal,
				});

				setTimeout(() => {
					resetCreateCustomerFileBillExchangeJournal();
					resetCreateCustomerFileCheckIssuanceJournal();
					resetCreateCustomerFileCheckRemittanceJournal();
				}, SUCCESS_ANIMATION_DELAY);
			})().catch((e) => {
				throw e;
			});
		},
		[
			billExchangeJournal?.id,
			checkIssuanceJournal?.id,
			checkRemittanceJournal?.id,
			createCustomerFileBillExchangeJournal,
			createCustomerFileCheckIssuanceJournal,
			createCustomerFileCheckRemittanceJournal,
			customerFileId,
			reset,
			resetCreateCustomerFileBillExchangeJournal,
			resetCreateCustomerFileCheckIssuanceJournal,
			resetCreateCustomerFileCheckRemittanceJournal,
		],
	);

	useEffect(() => {
		onHasDirtyFormChange(isDirty);
	}, [isDirty, onHasDirtyFormChange]);

	return (
		<SettingsDialogPage title="Journaux">
			<SettingsGroup>
				<Controller
					control={control}
					name="checkIssuanceJournal"
					render={({ field: { onChange, ...field } }) => {
						const handleChange = (changedValue?: JournalType | null) => onChange(changedValue);

						return (
							<SettingsItemAutocomplete<JournalType>
								{...field}
								dataSource={journalsDataSource}
								getOptionLabel={({ code, name: journalName }) => `${code} - ${journalName}`}
								label='Journal "Chèques émis"'
								onChange={handleChange}
								inheritedValue={inheritedCheckIssuanceJournal}
								disableClearable
							/>
						);
					}}
				/>
				<Controller
					control={control}
					name="checkRemittanceJournal"
					render={({ field: { onChange, ...field } }) => {
						const handleChange = (changedValue?: JournalType | null) => onChange(changedValue);

						return (
							<SettingsItemAutocomplete<JournalType>
								{...field}
								dataSource={journalsDataSource}
								getOptionLabel={({ code, name: journalName }) => `${code} - ${journalName}`}
								label='Journal "Remise de chèques"'
								onChange={handleChange}
								inheritedValue={inheritedCheckRemittanceJournal}
								disableClearable
							/>
						);
					}}
				/>
				<Controller
					control={control}
					name="billExchangeJournal"
					render={({ field: { onChange, ...field } }) => {
						const handleChange = (changedValue?: JournalType | null) => onChange(changedValue);

						return (
							<SettingsItemAutocomplete<JournalType>
								{...field}
								dataSource={journalsDataSource}
								getOptionLabel={({ code, name: journalName }) => `${code} - ${journalName}`}
								label='Journal "LCR émises"'
								onChange={handleChange}
								inheritedValue={inheritedBillExchangeJournal}
								disableClearable
							/>
						);
					}}
				/>
			</SettingsGroup>

			<ActionWrapper>
				<Button disabled={!isFormValid} onClick={handleSubmit(onSubmit)} variant="contained">
					{hasMutationSucceeded ? 'Journaux validés' : 'Valider'}
				</Button>

				{error && <ErrorWrapper>{error}</ErrorWrapper>}
			</ActionWrapper>
		</SettingsDialogPage>
	);
};

export default JournalCodes;
